import { extractString, extractNumber } from './sanitizeData';

export interface DataQualityIssueItem {
  id: string;
  field: string;
  severity: 'critical' | 'warning' | 'info';
  message: string;
  detectedAt: string;
  isResolved?: boolean;
}

export interface InvoiceQualitySummary {
  totalIssues: number;
  criticalCount: number;
  warningCount: number;
  issues: DataQualityIssueItem[];
  badgeType: 'clean' | 'warning' | 'critical' | 'pending';
  badgeLabel: string;
}

/**
 * Storage key helper for tracking resolved issues per invoice
 */
function getResolvedStorageKey(invoiceId: string): string {
  return `invoice_resolved_issues_${invoiceId}`;
}

export function getResolvedIssueIds(invoiceId: string): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(getResolvedStorageKey(invoiceId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function saveResolvedIssueIds(invoiceId: string, resolvedIds: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(getResolvedStorageKey(invoiceId), JSON.stringify(Array.from(resolvedIds)));
  } catch {
    // ignore local storage errors
  }
}

/**
 * Derives the complete list of Data Quality issues for an invoice
 */
export function deriveInvoiceIssues(
  invoice: unknown
): DataQualityIssueItem[] {
  const invObj = (invoice ?? {}) as Record<string, unknown>;
  const rawDq = invObj.data_quality as {
    total_issues?: number;
    issues_count?: number;
    critical_count?: number;
    warning_count?: number;
    badge_type?: string;
    issues?: Array<Record<string, unknown>>;
  } | undefined;

  // 1. If backend API explicitly returned data_quality with clean badge or 0 issues
  if (
    rawDq &&
    (rawDq.total_issues === 0 ||
      rawDq.issues_count === 0 ||
      rawDq.badge_type?.toLowerCase() === 'clean' ||
      (Array.isArray(rawDq.issues) && rawDq.issues.length === 0))
  ) {
    return [];
  }

  // 2. If backend API returned structured issues array, normalize and return them
  if (rawDq && Array.isArray(rawDq.issues) && rawDq.issues.length > 0) {
    const issues: DataQualityIssueItem[] = [];
    rawDq.issues.forEach((rawIssue, idx) => {
      const field = extractString(rawIssue.field || rawIssue.name || rawIssue.code, 'InvoiceField');
      const rawSev = extractString(rawIssue.severity || rawIssue.type, 'warning').toLowerCase();
      const severity: 'critical' | 'warning' | 'info' =
        rawSev === 'critical' || rawSev === 'error' ? 'critical' : rawSev === 'info' ? 'info' : 'warning';
      const message = extractString(rawIssue.message || rawIssue.description, 'Data issue detected');
      const detectedAt = extractString(rawIssue.detected_at || rawIssue.created_at, 'Detected by InvoiceParse AI · 1 day ago');
      const id = extractString(rawIssue.id || rawIssue.code, `${field}-${idx}`);

      issues.push({
        id,
        field,
        severity,
        message,
        detectedAt,
      });
    });
    return issues;
  }

  // 3. Fallback heuristic checks only when backend did NOT provide explicit data_quality
  const issues: DataQualityIssueItem[] = [];
  const vendorName = extractString(
    invObj.vendor_name ||
      ('vendor' in invObj && typeof invObj.vendor === 'object' && invObj.vendor !== null
        ? (invObj.vendor as Record<string, unknown>).name
        : '') ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? ((invObj.fields as Record<string, unknown>).vendor as Record<string, unknown>)?.name
        : '')
  );

  const invoiceId = extractString(
    invObj.invoice_id ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? (invObj.fields as Record<string, unknown>).invoice_id ||
          ((invObj.fields as Record<string, unknown>).invoice_meta as Record<string, unknown>)?.invoice_id
        : '')
  );

  const customerVat = extractString(
    invObj.customer_vat ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? (invObj.fields as Record<string, unknown>).customer_vat ||
          ((invObj.fields as Record<string, unknown>).customer as Record<string, unknown>)?.tax_id
        : '')
  );

  const subtotal = extractNumber(
    invObj.subtotal ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? (invObj.fields as Record<string, unknown>).subtotal ||
          ((invObj.fields as Record<string, unknown>).totals as Record<string, unknown>)?.subtotal
        : 0),
    0
  );

  const totalTax = extractNumber(
    invObj.total_tax ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? (invObj.fields as Record<string, unknown>).tax_amount ||
          ((invObj.fields as Record<string, unknown>).totals as Record<string, unknown>)?.tax_amount
        : 0),
    0
  );

  const invoiceTotal = extractNumber(
    ('invoice_total' in invObj && typeof invObj.invoice_total === 'object' && invObj.invoice_total !== null
      ? (invObj.invoice_total as Record<string, unknown>).amount
      : invObj.amount) ||
      ('fields' in invObj && typeof invObj.fields === 'object' && invObj.fields !== null
        ? ((invObj.fields as Record<string, unknown>).totals as Record<string, unknown>)?.invoice_total
        : 0),
    0
  );

  const items = Array.isArray(invObj.items) ? invObj.items : [];
  const lineItemsSum = items.reduce((sum, it) => {
    const itemAmount = typeof it === 'object' && it !== null ? extractNumber((it as Record<string, unknown>).amount, 0) : 0;
    return sum + itemAmount;
  }, 0);

  // Audit: Line items sum vs invoice total mismatch
  if (Math.abs(lineItemsSum - invoiceTotal) > 0.05 && lineItemsSum < invoiceTotal && invoiceTotal > 0) {
    const missing = (invoiceTotal - lineItemsSum).toFixed(2);
    issues.push({
      id: 'subtotal-lineitems-mismatch',
      field: 'SubTotal',
      severity: 'critical',
      message: `Line items sum (€${lineItemsSum.toFixed(2)}) is below invoice subtotal (€${invoiceTotal.toFixed(2)}) — missing €${missing}. Some line items may be incomplete.`,
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  // Audit: Subtotal missing
  if (subtotal === 0 && invoiceTotal > 0) {
    issues.push({
      id: 'subtotal-missing',
      field: 'SubTotal',
      severity: 'warning',
      message: 'Subtotal is missing from this invoice.',
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  // Audit: Total Tax missing
  if (totalTax === 0 && invoiceTotal > 0) {
    issues.push({
      id: 'totaltax-missing',
      field: 'TotalTax',
      severity: 'warning',
      message: 'Total Tax is missing from this invoice.',
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  // Audit: Invoice ID missing
  if (!invoiceId || invoiceId.trim() === '') {
    issues.push({
      id: 'invoiceid-missing',
      field: 'InvoiceId',
      severity: 'warning',
      message: 'Invoice Number is missing from this invoice.',
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  // Audit: Vendor Name missing
  if (!vendorName || vendorName.toLowerCase().includes('unknown')) {
    issues.push({
      id: 'vendorname-missing',
      field: 'VendorName',
      severity: 'warning',
      message: 'Vendor Name is missing from this invoice.',
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  // Audit: Customer VAT / Tax ID missing
  if (!customerVat || customerVat.trim() === '') {
    issues.push({
      id: 'customertaxid-missing',
      field: 'CustomerTaxId',
      severity: 'critical',
      message: 'Customer VAT/Tax ID is missing from this invoice. This field is required for tax compliance.',
      detectedAt: 'Detected by InvoiceParse AI · 1 day ago',
    });
  }

  return issues;
}

/**
 * Calculates summary metrics and badge state for an invoice
 */
export function getInvoiceQualitySummary(
  invoice: unknown
): InvoiceQualitySummary {
  const invObj = (invoice ?? {}) as Record<string, unknown>;
  const invoiceId = extractString(invObj.id, '');
  const resolvedIds = invoiceId ? getResolvedIssueIds(invoiceId) : new Set<string>();

  const rawStatus = invObj.status;
  if (
    typeof rawStatus === 'string' &&
    ['pending', 'processing', 'uploading'].includes(rawStatus.toLowerCase())
  ) {
    return {
      totalIssues: 0,
      criticalCount: 0,
      warningCount: 0,
      issues: [],
      badgeType: 'pending',
      badgeLabel: 'Processing',
    };
  }

  // 1. Direct Backend data_quality summary
  const rawDq = invObj.data_quality as {
    total_issues?: number;
    issues_count?: number;
    critical_count?: number;
    warning_count?: number;
    badge_type?: 'clean' | 'critical' | 'warning' | 'pending' | string;
    issues?: Array<Record<string, unknown>>;
  } | null | undefined;

  if (rawDq && typeof rawDq === 'object') {
    const rawTotal =
      rawDq.total_issues ??
      rawDq.issues_count ??
      (Array.isArray(rawDq.issues) ? rawDq.issues.length : null);
    const rawCrit = rawDq.critical_count ?? 0;
    const rawWarn = rawDq.warning_count ?? 0;
    const rawBadgeType = rawDq.badge_type?.toLowerCase();

    // Check if backend declares it clean (0 total issues or clean badge)
    if (rawTotal === 0 || rawBadgeType === 'clean') {
      return {
        totalIssues: 0,
        criticalCount: 0,
        warningCount: 0,
        issues: [],
        badgeType: 'clean',
        badgeLabel: '',
      };
    }

    if (rawTotal !== null && rawTotal > 0) {
      const derivedIssues = deriveInvoiceIssues(invoice);
      const effectiveTotal = Math.max(0, rawTotal - resolvedIds.size);

      const bType: 'critical' | 'warning' | 'clean' | 'pending' =
        rawBadgeType === 'critical' || rawCrit > 0
          ? 'critical'
          : rawBadgeType === 'warning' || rawWarn > 0
          ? 'warning'
          : rawBadgeType === 'pending'
          ? 'pending'
          : effectiveTotal === 0
          ? 'clean'
          : 'warning';

      return {
        totalIssues: effectiveTotal,
        criticalCount: rawCrit,
        warningCount: rawWarn,
        issues: derivedIssues.map((iss) => ({ ...iss, isResolved: resolvedIds.has(iss.id) })),
        badgeType: effectiveTotal === 0 ? 'clean' : bType,
        badgeLabel: effectiveTotal === 0 ? '' : String(effectiveTotal),
      };
    }
  }

  // 2. Fallback heuristic derivation when data_quality object is not provided by API
  const allIssues = deriveInvoiceIssues(invoice);
  const unresolvedIssues = allIssues.filter((iss) => !resolvedIds.has(iss.id));
  const totalIssues = unresolvedIssues.length;
  const criticalCount = unresolvedIssues.filter((iss) => iss.severity === 'critical').length;
  const warningCount = unresolvedIssues.filter((iss) => iss.severity !== 'critical').length;

  if (totalIssues === 0) {
    return {
      totalIssues: 0,
      criticalCount: 0,
      warningCount: 0,
      issues: allIssues.map((iss) => ({ ...iss, isResolved: resolvedIds.has(iss.id) })),
      badgeType: 'clean',
      badgeLabel: '',
    };
  }

  const badgeType: 'critical' | 'warning' | 'clean' | 'pending' =
    criticalCount > 0 ? 'critical' : 'warning';

  return {
    totalIssues,
    criticalCount,
    warningCount,
    issues: allIssues.map((iss) => ({ ...iss, isResolved: resolvedIds.has(iss.id) })),
    badgeType,
    badgeLabel: String(totalIssues),
  };
}
